import { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import RawCard from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import RawTableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { styled, useTheme } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { Button, HrefLink, InformationMessage, Modal, Switch, Tag } from '@bloomays-lib/ui.shared';
import {
  GET_ONE_FULL_MISSION,
  CREATE_PURCHASE_ORDER,
  UPDATE_MISSION,
  UPDATE_PURCHASE_ORDER,
  UPLOAD_PURCHASE_ORDER_FILE,
  UPSERT_SOCIETY,
} from '@bloomays-lib/adapter.api-bloomer';
import { IMissionCardDetails, ISociety, PurchaseOrder } from '@bloomays-lib/types.shared';
import {
  computeMissionPurchaseOrderIndicator,
  MissionPurchaseOrderIndicator,
} from '../../helpers/missionPurchaseOrderStatus';
import {
  derivePurchaseOrderDisplayStatusForReferenceDate,
  PurchaseOrderDisplayStatus,
} from '../../helpers/purchaseOrderDashboard';
import {
  getMissionPurchaseOrderIndicatorPalette,
  getPurchaseOrderDisplayStatusPalette,
} from '../../helpers/purchaseOrderSemanticStatus';
import { cancel, notify } from '../../helpers/toastify';
import { errorLogger } from '../../helpers/error';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import {
  PurchaseOrderFormModal,
  PurchaseOrderFormSubmitContext,
  PurchaseOrderFormValues,
} from './PurchaseOrderFormModal';

export type MissionPurchaseOrdersSectionProps = {
  mission: IMissionCardDetails;
  clientBillingSociety?: ISociety;
};

const toDateInput = (value: Date | string | null | undefined): string | undefined =>
  value ? convertDateToStringFormat(value as string, 'yyyy-MM-dd') : undefined;

const formatDate = (value: Date | string | undefined): string => {
  if (!value) return '—';
  return convertDateToStringFormat(value as string, 'dd/MM/yyyy');
};

const formatAmount = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '—';
  return `${value.toLocaleString('fr-FR')} €`;
};

export const MissionPurchaseOrdersSection = ({ mission, clientBillingSociety }: MissionPurchaseOrdersSectionProps) => {
  const { t } = useTranslation(['missionPurchaseOrder', 'mission', 'notify']);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editTarget, setEditTarget] = useState<PurchaseOrder | null>(null);
  const [pendingMissionFlag, setPendingMissionFlag] = useState(false);
  const [pendingClientFlag, setPendingClientFlag] = useState(false);
  const [pendingForm, setPendingForm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const [confirmClientChangeOpen, setConfirmClientChangeOpen] = useState(false);
  const [pendingClientChangeValue, setPendingClientChangeValue] = useState<boolean | null>(null);

  const purchaseOrders = mission.purchaseOrders ?? [];
  const effectivePurchaseOrderRequired = mission.effectivePurchaseOrderRequired ?? false;

  const indicator = useMemo<MissionPurchaseOrderIndicator>(
    () =>
      computeMissionPurchaseOrderIndicator({
        effectivePurchaseOrderRequired,
        purchaseOrders,
      }),
    [effectivePurchaseOrderRequired, purchaseOrders],
  );

  const indicatorLabel = useMemo<string>(() => {
    switch (indicator) {
      case 'ok':
        return t('indicatorOk');
      case 'missing':
        return t('indicatorMissing');
      case 'expired':
        return t('indicatorExpired');
      case 'exhausted':
        return t('indicatorExhausted');
      case 'notRequired':
      default:
        return t('indicatorNotRequired');
    }
  }, [indicator, t]);

  const indicatorReason = useMemo<string | null>(() => {
    switch (indicator) {
      case 'ok':
        return t('reasonOk');
      case 'missing':
        return t('reasonMissing');
      case 'expired':
        return t('reasonExpired');
      case 'exhausted':
        return t('reasonExhausted');
      case 'notRequired':
      default:
        return null;
    }
  }, [indicator, t]);

  const refetchMission = [
    {
      query: GET_ONE_FULL_MISSION,
      variables: { recordId: mission.recordId },
    },
  ];

  const [updateMission] = useMutation(UPDATE_MISSION, { refetchQueries: refetchMission });
  const [upsertSociety] = useMutation(UPSERT_SOCIETY, { refetchQueries: refetchMission });
  const [createPurchaseOrder] = useMutation(CREATE_PURCHASE_ORDER, { refetchQueries: refetchMission });
  const [updatePurchaseOrder] = useMutation(UPDATE_PURCHASE_ORDER, { refetchQueries: refetchMission });
  const [uploadPurchaseOrderFile] = useMutation(UPLOAD_PURCHASE_ORDER_FILE, { refetchQueries: refetchMission });

  const createInitialValues = useMemo<Partial<PurchaseOrderFormValues>>(
    () => ({
      missionsRecordId: [mission.recordId],
      initialMissions: [
        {
          id: mission.recordId,
          name: mission.label ?? mission.recordId,
          clientSocietyRecordId: clientBillingSociety?.recordId,
        },
      ],
      clientSocietyRecordId: clientBillingSociety?.recordId,
      clientSocietyName: clientBillingSociety?.name,
      startDate: mission.beginMission ?? undefined,
      endDate: mission.endMission ?? undefined,
    }),
    [mission, clientBillingSociety],
  );

  const editInitialValues = useMemo<Partial<PurchaseOrderFormValues> | undefined>(() => {
    if (!editTarget) {
      return undefined;
    }
    const missionIds = editTarget.missionsRecordId ?? [mission.recordId];
    return {
      orderNumber: editTarget.orderNumber,
      clientSocietyRecordId: editTarget.clientSocietyRecordId ?? clientBillingSociety?.recordId,
      clientSocietyName: clientBillingSociety?.name,
      missionsRecordId: missionIds,
      initialMissions: missionIds.map((missionRecordId) => ({
        id: missionRecordId,
        name: missionRecordId === mission.recordId ? mission.label ?? missionRecordId : missionRecordId,
        clientSocietyRecordId: editTarget.clientSocietyRecordId ?? clientBillingSociety?.recordId,
      })),
      startDate: toDateInput(editTarget.startDate),
      endDate: toDateInput(editTarget.endDate),
      amountHT: editTarget.amountHT,
      invoicedAmountHT: editTarget.invoicedAmountHT,
      invoiceComment: editTarget.invoiceComment,
      useForNonWorkingHours: editTarget.useForNonWorkingHours,
    };
  }, [editTarget, mission, clientBillingSociety]);

  const openCreateForm = () => {
    setFormMode('create');
    setEditTarget(null);
    setFormOpen(true);
  };

  const openEditForm = (purchaseOrder: PurchaseOrder) => {
    if (!purchaseOrder.recordId) {
      return;
    }
    setFormMode('edit');
    setEditTarget(purchaseOrder);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditTarget(null);
  };

  const handleUploadFile = async (file: File) => {
    if (!editTarget?.recordId) {
      return;
    }
    try {
      setUploadPending(true);
      notify('info', t('updateInProgress', { ns: 'notify' }), null, {
        autoClose: 600,
        hideProgressBar: true,
      });
      const uploadResult = await uploadPurchaseOrderFile({
        variables: { input: { recordId: editTarget.recordId, file } },
      });
      const updatedPurchaseOrder = uploadResult.data?.uploadPurchaseOrderFile;
      if (updatedPurchaseOrder) {
        setEditTarget(updatedPurchaseOrder);
      }
      notify('success', t('uploadSuccess', { ns: 'purchaseOrderDashboard' }));
    } catch (error) {
      cancel();
      errorLogger(error as Error, { extraInfos: 'TEC-2888 upload purchase order file' });
      notify('error', t('uploadError', { ns: 'purchaseOrderDashboard' }));
    } finally {
      setUploadPending(false);
    }
  };

  const handleSubmitForm = async (values: PurchaseOrderFormValues, context?: PurchaseOrderFormSubmitContext) => {
    try {
      setPendingForm(true);
      notify('info', t('updateInProgress', { ns: 'notify' }), null, {
        autoClose: 600,
        hideProgressBar: true,
      });
      if (formMode === 'create') {
        let createdPurchaseOrder: PurchaseOrder | undefined;
        try {
          const createResult = await createPurchaseOrder({
            variables: {
              input: {
                orderNumber: values.orderNumber,
                clientSocietyRecordId: values.clientSocietyRecordId ?? clientBillingSociety?.recordId,
                missionsRecordId: values.missionsRecordId ?? [mission.recordId],
                startDate: values.startDate,
                endDate: values.endDate,
                amountHT: values.amountHT,
                useForNonWorkingHours: values.useForNonWorkingHours,
                invoiceComment: values.invoiceComment,
              },
            },
          });
          createdPurchaseOrder = createResult.data?.createPurchaseOrder;
        } catch (error) {
          cancel();
          errorLogger(error as Error, { extraInfos: 'TEC-2888 create purchase order' });
          notify('error', t('createError', { ns: 'purchaseOrderDashboard' }));
          return;
        }

        const createdRecordId = createdPurchaseOrder?.recordId;
        if (context?.file && createdRecordId) {
          try {
            await uploadPurchaseOrderFile({
              variables: { input: { recordId: createdRecordId, file: context.file } },
            });
            notify('success', t('uploadSuccess', { ns: 'purchaseOrderDashboard' }));
          } catch (error) {
            cancel();
            errorLogger(error as Error, { extraInfos: 'TEC-2888 upload purchase order file after create' });
            notify('error', t('createUploadError', { ns: 'purchaseOrderDashboard' }));
            if (createdPurchaseOrder) {
              setFormMode('edit');
              setEditTarget(createdPurchaseOrder);
            }
            return;
          }
        } else {
          notify('success', t('createSuccess', { ns: 'purchaseOrderDashboard' }));
        }
      } else if (editTarget?.recordId) {
        await updatePurchaseOrder({
          variables: {
            input: {
              recordId: editTarget.recordId,
              orderNumber: values.orderNumber,
              clientSocietyRecordId: values.clientSocietyRecordId ?? clientBillingSociety?.recordId,
              missionsRecordId: values.missionsRecordId,
              startDate: values.startDate,
              endDate: values.endDate,
              amountHT: values.amountHT,
              invoicedAmountHT: values.invoicedAmountHT,
              useForNonWorkingHours: values.useForNonWorkingHours,
              invoiceComment: values.invoiceComment,
            },
          },
        });
        notify('success', t('updateSuccess', { ns: 'purchaseOrderDashboard' }));
      }
      closeForm();
    } catch (error) {
      cancel();
      errorLogger(error as Error, { extraInfos: 'TEC-2888 save purchase order' });
      notify('error', t('updateError', { ns: 'purchaseOrderDashboard' }));
    } finally {
      setPendingForm(false);
    }
  };

  const handleToggleMissionFlag = async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    try {
      setPendingMissionFlag(true);
      notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 600, hideProgressBar: true });
      await updateMission({
        variables: {
          input: {
            recordId: mission.recordId,
            purchaseOrderRequired: checked,
          },
        },
      });
      notify('success', t('updateSuccess', { ns: 'notify' }));
    } catch (error) {
      cancel();
      errorLogger(error as Error, { extraInfos: 'TEC-2888 update mission purchaseOrderRequired' });
      notify('error', t('randomUpdateError', { ns: 'notify' }));
    } finally {
      setPendingMissionFlag(false);
    }
  };

  const requestClientFlagChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!clientBillingSociety?.recordId) return;
    setPendingClientChangeValue(checked);
    setConfirmClientChangeOpen(true);
  };

  const confirmClientFlagChange = async () => {
    if (!clientBillingSociety?.recordId || pendingClientChangeValue === null) {
      setConfirmClientChangeOpen(false);
      setPendingClientChangeValue(null);
      return;
    }
    try {
      setPendingClientFlag(true);
      notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 600, hideProgressBar: true });
      await upsertSociety({
        variables: {
          input: {
            recordId: clientBillingSociety.recordId,
            name: clientBillingSociety.name,
            siret: clientBillingSociety.siret,
            orderNumberRequired: pendingClientChangeValue,
          },
        },
      });
      notify('success', t('updateSuccess', { ns: 'notify' }));
    } catch (error) {
      cancel();
      errorLogger(error as Error, { extraInfos: 'TEC-2888 update society orderNumberRequired' });
      notify('error', t('randomUpdateError', { ns: 'notify' }));
    } finally {
      setPendingClientFlag(false);
      setConfirmClientChangeOpen(false);
      setPendingClientChangeValue(null);
    }
  };

  return (
    <Card>
      <CardContent>
        <CardHeader
          title={t('sectionTitle')}
          titleTypographyProps={{ variant: 'heading6' }}
          sx={{ paddingLeft: 0, paddingRight: 0 }}
        />

        <IndicatorBox>
          <IndicatorTag indicator={indicator} text={indicatorLabel} />
          {indicatorReason ? <ReasonText variant="body1Light">{indicatorReason}</ReasonText> : null}
        </IndicatorBox>

        <FlagsStack direction="column" spacing={1}>
          <Switch
            id="mission-purchase-order-required"
            label={t('purchaseOrderRequiredLabel', { ns: 'mission' })}
            checked={mission.purchaseOrderRequired === true}
            onSwitch={handleToggleMissionFlag}
            disabled={pendingMissionFlag}
          />
          {clientBillingSociety ? (
            <Switch
              id="client-order-number-required"
              label={t('purchaseOrderRequiredClientLabel', { ns: 'mission' })}
              checked={clientBillingSociety.orderNumberRequired === true}
              onSwitch={requestClientFlagChange}
              disabled={pendingClientFlag}
            />
          ) : null}
          {clientBillingSociety ? (
            <InformationMessage text={t('purchaseOrderRequiredClientWarning', { ns: 'mission' })} />
          ) : null}
        </FlagsStack>

        <HistoryHeader>
          <Typography variant="heading6">{t('history')}</Typography>
          <Button
            ariaLabel="mission-purchase-order-quick-create"
            textButton={t('newPurchaseOrder', { ns: 'purchaseOrderDashboard' })}
            onClick={openCreateForm}
            disable={!clientBillingSociety?.recordId}
          />
        </HistoryHeader>

        {purchaseOrders.length === 0 ? (
          <EmptyText>{t('noPurchaseOrders')}</EmptyText>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('orderNumberLabel')}</TableCell>
                  <TableCell>{t('statusLabel')}</TableCell>
                  <TableCell align="right">{t('amountHTLabel')}</TableCell>
                  <TableCell align="right">{t('invoicedAmountHTLabel')}</TableCell>
                  <TableCell align="right">{t('remainingAmountHTLabel')}</TableCell>
                  <TableCell>{t('startDateLabel')}</TableCell>
                  <TableCell>{t('endDateLabel')}</TableCell>
                  <TableCell>{t('fileLabel')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseOrders.map((purchaseOrder) => (
                  <PurchaseOrderRow
                    key={purchaseOrder.recordId ?? purchaseOrder.id}
                    purchaseOrder={purchaseOrder}
                    onEdit={openEditForm}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {formOpen ? (
        <PurchaseOrderFormModal
          open
          mode={formMode}
          recordId={formMode === 'edit' ? editTarget?.recordId : undefined}
          fileUrl={formMode === 'edit' ? editTarget?.fileUrl : undefined}
          initialValues={formMode === 'edit' ? editInitialValues : createInitialValues}
          pending={pendingForm}
          uploadPending={uploadPending}
          onSubmit={handleSubmitForm}
          onUploadFile={formMode === 'edit' ? handleUploadFile : undefined}
          onClose={closeForm}
        />
      ) : null}

      <Modal
        open={confirmClientChangeOpen}
        onClose={() => {
          setConfirmClientChangeOpen(false);
          setPendingClientChangeValue(null);
        }}
        additionnalCSS={{ width: '50%', padding: '32px', borderRadius: '20px' }}
      >
        <ConfirmContainer>
          <Typography variant="heading6">{t('purchaseOrderRequiredClientLabel', { ns: 'mission' })}</Typography>
          <InformationMessage text={t('purchaseOrderRequiredClientWarning', { ns: 'mission' })} />
          {clientBillingSociety ? (
            <Typography variant="body1Light">
              <Trans i18nKey="purchaseOrderRequiredClientLabel" ns="mission" />:{' '}
              <strong>{clientBillingSociety.name}</strong>
            </Typography>
          ) : null}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              textButton={t('createCancel')}
              onClick={() => {
                setConfirmClientChangeOpen(false);
                setPendingClientChangeValue(null);
              }}
            />
            <Button
              ariaLabel="confirm-client-order-number-required"
              textButton={t('createModify')}
              onClick={confirmClientFlagChange}
              disable={pendingClientFlag}
            />
          </Stack>
        </ConfirmContainer>
      </Modal>
    </Card>
  );
};

type PurchaseOrderRowProps = {
  purchaseOrder: PurchaseOrder;
  onEdit: (purchaseOrder: PurchaseOrder) => void;
};

const DISPLAY_STATUS_TRANSLATION_KEYS: Record<PurchaseOrderDisplayStatus, string> = {
  received: 'statusReceived',
  partial: 'statusPartial',
  waiting: 'statusWaiting',
  expired: 'statusExpired',
  soldout: 'statusSoldout',
};

const PurchaseOrderRow = ({ purchaseOrder, onEdit }: PurchaseOrderRowProps) => {
  const { t } = useTranslation(['purchaseOrderDashboard']);
  const theme = useTheme();
  const displayStatus = derivePurchaseOrderDisplayStatusForReferenceDate(purchaseOrder);
  const palette = getPurchaseOrderDisplayStatusPalette(theme, displayStatus);
  const isEditable = !!purchaseOrder.recordId;

  return (
    <ClickableTableRow
      hover={isEditable}
      data-testid={purchaseOrder.recordId ? `mission-purchase-order-row-${purchaseOrder.recordId}` : undefined}
      aria-label={isEditable ? `${t('editAction')} ${purchaseOrder.orderNumber}` : undefined}
      onClick={isEditable ? () => onEdit(purchaseOrder) : undefined}
      $clickable={isEditable}
    >
      <TableCell>{purchaseOrder.orderNumber}</TableCell>
      <TableCell>
        <Tag
          text={t(DISPLAY_STATUS_TRANSLATION_KEYS[displayStatus])}
          backgroundColor={palette.background}
          color={palette.color}
        />
      </TableCell>
      <TableCell align="right">{formatAmount(purchaseOrder.amountHT)}</TableCell>
      <TableCell align="right">{formatAmount(purchaseOrder.invoicedAmountHT)}</TableCell>
      <TableCell align="right">
        {formatAmount(
          purchaseOrder.amountHT !== undefined && purchaseOrder.amountHT !== null
            ? purchaseOrder.amountHT - (purchaseOrder.invoicedAmountHT ?? 0)
            : undefined,
        )}
      </TableCell>
      <TableCell>{formatDate(purchaseOrder.startDate)}</TableCell>
      <TableCell>{formatDate(purchaseOrder.endDate)}</TableCell>
      <TableCell>
        {purchaseOrder.fileUrl ? (
          <HrefLink href={purchaseOrder.fileUrl} target="_blank" onClick={(event) => event.stopPropagation()}>
            {t('viewFile')}
          </HrefLink>
        ) : (
          '—'
        )}
      </TableCell>
    </ClickableTableRow>
  );
};

type IndicatorTagProps = {
  indicator: MissionPurchaseOrderIndicator;
  text: string;
};

const IndicatorTag = ({ indicator, text }: IndicatorTagProps) => {
  const theme = useTheme();
  const palette = getMissionPurchaseOrderIndicatorPalette(theme, indicator);
  return (
    <IndicatorTagBox data-testid={`mission-purchase-order-indicator-${indicator}`}>
      <Tag text={text} backgroundColor={palette.background} color={palette.color} />
    </IndicatorTagBox>
  );
};

const Card = styled(RawCard)(() => ({
  width: '100%',
  margin: 20,
  padding: 16,
}));

const IndicatorBox = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginBottom: 16,
}));

const IndicatorTagBox = styled('div')(() => ({
  display: 'inline-flex',
}));

const ReasonText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#374151',
}));

const FlagsStack = styled(Stack)(() => ({
  marginBottom: 16,
}));

const HistoryHeader = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 12,
}));

const EmptyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#374151',
  fontStyle: 'italic',
}));

const TableContainer = styled(RawTableContainer)(() => ({
  maxWidth: '100%',
  overflowX: 'auto',
}));

const ClickableTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== '$clickable',
})<{ $clickable: boolean }>(({ theme, $clickable }) => ({
  ...($clickable
    ? {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.neutral?.[10] ?? '#f3f4f6',
        },
      }
    : {}),
}));

const ConfirmContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}));

export default MissionPurchaseOrdersSection;
